import { useI18nSubscription } from '../../i18n/useI18nSubscription.js';
import { i18n } from '../../../i18n/index.js';
import { useState, type ReactElement } from 'react';
import { Plus } from 'lucide-react';
import { Button, Typography } from 'antd';

import type { TaskKanbanCardModel, TaskKanbanColumnModel } from '../lib/taskKanban.js';
import { TaskKanbanCard } from './TaskKanbanCard.js';

const { Text } = Typography;

/** Spec D `--font-mono`: счётчики и даты канбана — mono + tabular-nums. */
const MONO_FONT = 'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace';

export interface TaskKanbanBoardProps {
  columns: readonly TaskKanbanColumnModel[];
  isUpdating: boolean;
  onCreateInStatus: (status: TaskKanbanColumnModel['status']) => void;
  onMoveTask: (taskId: string, toStatus: TaskKanbanColumnModel['status']) => void;
}

export function TaskKanbanBoard({
  columns,
  isUpdating,
  onCreateInStatus,
  onMoveTask,
}: TaskKanbanBoardProps): ReactElement {
  useI18nSubscription();
  const [draggedCard, setDraggedCard] = useState<TaskKanbanCardModel | null>(null);

  return (
    <div
      data-testid="tasks-kanban-board"
      className="crm-radius-l w-full overflow-hidden"
      style={{
        border: '1px solid var(--ant-color-border-secondary)',
        background: 'var(--ant-color-bg-container)',
      }}
    >
      <div className="flex overflow-x-auto">
        {columns.map((column, index) => (
          <section
            key={column.status}
            data-testid={`task-kanban-column-${column.status}`}
            className="w-72 shrink-0"
            style={{
              ...(index > 0 ? { borderLeft: '1px solid var(--ant-color-border-secondary)' } : {}),
            }}
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => {
              event.preventDefault();
              if (draggedCard !== null && draggedCard.status !== column.status) {
                onMoveTask(draggedCard.taskId, column.status);
              }
              setDraggedCard(null);
            }}
          >
            {/* Spec D `.pipe-col-head`: uppercase micro-label + mono count. */}
            <header
              className="flex items-center gap-2"
              style={{
                padding: '9px 12px',
                borderBottom: '1px solid var(--ant-color-border-secondary)',
              }}
            >
              <h2
                className="m-0 truncate"
                style={{
                  fontSize: 10.5,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.07em',
                  color: 'var(--ant-color-text-secondary)',
                }}
              >
                {column.title}
              </h2>
              <span
                className="min-w-0 flex-1"
                style={{ height: 1, background: 'var(--ant-color-border-secondary)' }}
              />
              <span
                style={{
                  fontSize: 10.5,
                  color: 'var(--ant-color-text-tertiary)',
                  fontFamily: MONO_FONT,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {column.count}
              </span>
              <Button
                type="text"
                size="small"
                aria-label={i18n.t(
                  'crm.features.dynamic.features.tasks.components.taskkanbanboard.76',
                  { p0: column.title },
                )}
                icon={<Plus size={14} />}
                onClick={() => onCreateInStatus(column.status)}
                style={{
                  width: 24,
                  height: 24,
                  padding: 0,
                  color: 'var(--ant-color-text-secondary)',
                }}
              />
            </header>

            <div className="flex flex-col" style={{ padding: '8px 8px 10px', gap: 6 }}>
              {column.cards.length > 0 ? (
                column.cards.map((card) => (
                  <TaskKanbanCard
                    key={card.taskId}
                    card={card}
                    isUpdating={isUpdating}
                    onDragStart={setDraggedCard}
                  />
                ))
              ) : (
                <div
                  className="crm-radius-s flex items-center justify-center"
                  style={{
                    border: '1px dashed var(--ant-color-border-secondary)',
                    padding: '18px 12px',
                  }}
                >
                  <Text style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)' }}>
                    {i18n.t(
                      'crm.features.features.tasks.components.taskkanbanboard.в_этом_статусе_пока_нет_задач',
                    )}
                  </Text>
                </div>
              )}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
